import { useEffect, useState, type CSSProperties } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BookOpen, Clock, ExternalLink, Share2, User } from "lucide-react";
import { useData } from "@/contexts/DataContext";
import { sanitizeRichTextHtml } from "@/lib/htmlSanitizer";
import { toWordPreview } from "@/lib/textPreview";
import YouTubeEmbed from "@/components/YouTubeEmbed";
import { buildShareUrl } from "@/lib/shareUrl";
import { shareContent } from "@/lib/shareContent";

const fontFamilyByStyle: Record<string, string> = {
  normal: "'Inter', system-ui, sans-serif",
  times_new_roman: "'Times New Roman', Times, serif",
  courier_new: "'Courier New', Courier, monospace",
};

const LearningDetail = () => {
  const { id } = useParams();
  const { learning } = useData();
  const [isSharing, setIsSharing] = useState(false);
  const [shareMessage, setShareMessage] = useState("");
  // Find learning item by slug first, then by ID for backward compatibility
  const item = learning.find((entry) => entry.slug === id) || learning.find((entry) => entry.id === id);

  const safeDescription = sanitizeRichTextHtml(item?.description || "");
  const textAlign = (item?.contentAlignment || "justify") as CSSProperties["textAlign"];
  const fontFamily = fontFamilyByStyle[item?.fontStyle || "times_new_roman"] || fontFamilyByStyle.times_new_roman;
  const contentStyle: CSSProperties = { textAlign, fontFamily };

  useEffect(() => {
    const faviconHref = item?.imageUrl ? String(item.imageUrl).trim() : "data:,";
    const selectors = 'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]';
    const existingLinks = Array.from(document.head.querySelectorAll<HTMLLinkElement>(selectors));
    const snapshots = existingLinks.map((el) => ({
      el,
      href: el.getAttribute("href"),
      rel: el.getAttribute("rel"),
      type: el.getAttribute("type"),
      sizes: el.getAttribute("sizes"),
    }));
    const createdLinks: HTMLLinkElement[] = [];
    const targets = existingLinks.length > 0 ? existingLinks : (() => {
      const link = document.createElement("link");
      link.setAttribute("rel", "icon");
      document.head.appendChild(link);
      createdLinks.push(link);
      return [link];
    })();

    targets.forEach((linkEl) => {
      linkEl.setAttribute("href", faviconHref);
      const rel = String(linkEl.getAttribute("rel") || "").toLowerCase();
      if (rel === "icon" && !linkEl.getAttribute("sizes")) {
        linkEl.setAttribute("sizes", "any");
      }
    });

    return () => {
      snapshots.forEach(({ el, href, rel, type, sizes }) => {
        if (href === null) {
          el.removeAttribute("href");
        } else {
          el.setAttribute("href", href);
        }

        if (rel === null) {
          el.removeAttribute("rel");
        } else {
          el.setAttribute("rel", rel);
        }

        if (type === null) {
          el.removeAttribute("type");
        } else {
          el.setAttribute("type", type);
        }

        if (sizes === null) {
          el.removeAttribute("sizes");
        } else {
          el.setAttribute("sizes", sizes);
        }
      });

      createdLinks.forEach((el) => el.remove());
    };
  }, [item?.imageUrl]);

  const handleShare = async () => {
    if (!item) {
      return;
    }

    setIsSharing(true);
    setShareMessage("");

    const shareUrl = buildShareUrl("learning", item.slug || item.id);
    const result = await shareContent({
      title: item.title,
      text: toWordPreview(item.description, 20),
      url: shareUrl,
      imageUrl: item.imageUrl,
    });

    if (result.status === "copied") {
      setShareMessage("Learning link copied to clipboard.");
    } else if (result.status === "unsupported") {
      setShareMessage("Sharing is not supported in this browser.");
    } else if (result.status === "error") {
      setShareMessage("Unable to share this learning content right now.");
    }

    setIsSharing(false);
  };

  if (!item) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <p className="text-muted-foreground">Learning details not found.</p>
        <Link to="/learning" className="text-secondary hover:underline mt-4 inline-block">
          {"<- Back to Learning"}
        </Link>
      </div>
    );
  }

  return (
    <div className="py-10 md:py-14">
      <div className="container mx-auto px-4 max-w-3xl">
        <Link to="/learning" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6" style={contentStyle}>
          <ArrowLeft size={16} /> Back to Learning
        </Link>

        {item.imageUrl && (
          <div className="mb-6">
            <img
              src={item.imageUrl}
              alt={item.title}
              className="w-full h-64 md:h-80 object-cover rounded-lg border border-border"
              onError={(event) => {
                event.currentTarget.src = "/placeholder.svg";
              }}
            />
          </div>
        )}

        <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-3 mt-1" style={contentStyle}>
          {item.title}
        </h1>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className={`px-2 py-1 rounded text-xs font-medium ${
            item.difficultyLevel === "beginner"
              ? "bg-green-100 text-green-800"
              : item.difficultyLevel === "intermediate"
              ? "bg-yellow-100 text-yellow-800"
              : "bg-red-100 text-red-800"
          }`}>
            {item.difficultyLevel}
          </span>
          <span className="badge-info">{item.category}</span>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-muted-foreground mb-6" style={contentStyle}>
          <div className="flex flex-wrap items-center gap-4">
            <span className="flex items-center gap-1">
              <User size={14} /> {item.instructor}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={14} /> {item.duration}
            </span>
            <span className="flex items-center gap-1">
              <BookOpen size={14} /> {item.status}
            </span>
          </div>
          <button
            type="button"
            onClick={() => void handleShare()}
            disabled={isSharing}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-input bg-background text-foreground hover:bg-muted disabled:opacity-60"
          >
            <Share2 size={14} />
            {isSharing ? "Sharing..." : "Share"}
          </button>
        </div>

        {item.videoUrl && (
          <div className="mb-6">
            <YouTubeEmbed url={item.videoUrl} title={item.title} />
          </div>
        )}

        <div className="prose prose-sm max-w-none text-foreground" style={contentStyle}>
          <div className="news-richtext text-base leading-relaxed" dangerouslySetInnerHTML={{ __html: safeDescription }} />
        </div>

        {item.resourceUrl && (
          <div className="mt-6">
            <a
              href={item.resourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
            >
              Start Learning <ExternalLink size={14} />
            </a>
          </div>
        )}

        {shareMessage && <p className="text-xs text-muted-foreground mt-4">{shareMessage}</p>}

        <div className="mt-8 p-4 rounded-lg bg-muted/50 border border-border" style={contentStyle}>
          <p className="text-xs text-muted-foreground">
            <strong>Disclaimer:</strong> Learning content is for educational purposes. Validate investment decisions independently.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LearningDetail;
